import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Table, message, Typography, Space, Alert, Tag, Input as AntInput } from 'antd';
import axios from 'axios';
import { API_URL } from '../config';

const { Text, Paragraph } = Typography;
const { Search } = AntInput;

interface AssignFeatureToRoomsModalProps {
  visible: boolean;
  hotelId: number;
  featureMewsId: string;
  featureName: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface RoomRow {
  id: number;
  name: string;
  floorNumber: string | null;
  roomType?: string;
}

const AssignFeatureToRoomsModal: React.FC<AssignFeatureToRoomsModalProps> = ({
  visible,
  hotelId,
  featureMewsId,
  featureName,
  onClose,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [rooms, setRooms] = useState<RoomRow[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [selectedRoomIds, setSelectedRoomIds] = useState<number[]>([]);
  const [originalAssignedIds, setOriginalAssignedIds] = useState<number[]>([]);
  const [roomSearch, setRoomSearch] = useState('');

  useEffect(() => {
    if (visible && hotelId !== undefined && featureMewsId) {
      loadRooms();
    } else if (!visible) {
      setSelectedRoomIds([]);
      setOriginalAssignedIds([]);
      setRoomSearch('');
      setRooms([]);
    }
  }, [visible, hotelId, featureMewsId]);

  const loadRooms = async () => {
    setLoadingRooms(true);
    try {
      // `/hotels/:id/rooms` flat payload lacks assignedFeatures; fetch per-room detail
      // in parallel so we can pre-check currently-assigned rooms.
      const listResp = await axios.get(`${API_URL}/hotels/${hotelId}/rooms`);
      const list: any[] = listResp.data;

      const ids = Array.from(
        new Set(list.map((r) => r.id).filter((id) => typeof id === 'number')),
      ) as number[];

      const details = await Promise.all(
        ids.map((id) =>
          axios.get(`${API_URL}/rooms/${id}`).then((r) => r.data).catch(() => null),
        ),
      );

      const byId = new Map<number, any>();
      const listById = new Map<number, any>();
      for (const item of list) {
        if (typeof item.id === 'number' && !listById.has(item.id)) {
          listById.set(item.id, item);
        }
      }
      for (const d of details) {
        if (d && typeof d.id === 'number') byId.set(d.id, d);
      }

      const rows: RoomRow[] = [];
      const assigned: number[] = [];
      for (const id of ids) {
        const detail = byId.get(id);
        const item = listById.get(id);
        const assignedFeatures: string[] = Array.isArray(detail?.assignedFeatures)
          ? detail.assignedFeatures
          : [];
        if (assignedFeatures.includes(featureMewsId)) assigned.push(id);
        rows.push({
          id,
          name: item?.room || detail?.name || `Room ${id}`,
          floorNumber: item?.floor || detail?.floorNumber || null,
          roomType: item?.roomType && item.roomType !== '-' ? item.roomType : undefined,
        });
      }

      rows.sort((a, b) => {
        const aNum = parseInt(a.name, 10);
        const bNum = parseInt(b.name, 10);
        if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
        return a.name.localeCompare(b.name);
      });

      setRooms(rows);
      setOriginalAssignedIds(assigned);
      setSelectedRoomIds(assigned);
    } catch (error) {
      console.error('Failed to load rooms:', error);
      message.error('Failed to load rooms');
    } finally {
      setLoadingRooms(false);
    }
  };

  const { toAdd, toRemove } = useMemo(() => {
    const origSet = new Set(originalAssignedIds);
    const selSet = new Set(selectedRoomIds);
    const add: number[] = [];
    const remove: number[] = [];
    for (const id of selectedRoomIds) if (!origSet.has(id)) add.push(id);
    for (const id of originalAssignedIds) if (!selSet.has(id)) remove.push(id);
    return { toAdd: add, toRemove: remove };
  }, [selectedRoomIds, originalAssignedIds]);

  const hasChanges = toAdd.length > 0 || toRemove.length > 0;

  const handleSubmit = async () => {
    if (!hasChanges) {
      message.info('No changes to save');
      return;
    }

    setSubmitting(true);
    try {
      let added = 0;
      let removed = 0;
      let alreadyAssigned = 0;
      let notAssigned = 0;

      if (toAdd.length > 0) {
        const resp = await axios.post(`${API_URL}/rooms/bulk-assign-feature`, {
          hotelId,
          featureMewsId,
          roomIds: toAdd,
        });
        added = resp.data.updated || 0;
        alreadyAssigned = resp.data.alreadyAssigned || 0;
      }

      if (toRemove.length > 0) {
        const resp = await axios.post(`${API_URL}/rooms/bulk-unassign-feature`, {
          hotelId,
          featureMewsId,
          roomIds: toRemove,
        });
        removed = resp.data.updated || 0;
        notAssigned = resp.data.notAssigned || 0;
      }

      const parts: string[] = [];
      if (added > 0) parts.push(`added to ${added}`);
      if (removed > 0) parts.push(`removed from ${removed}`);
      if (alreadyAssigned > 0) parts.push(`${alreadyAssigned} already had it`);
      if (notAssigned > 0) parts.push(`${notAssigned} didn't have it`);
      message.success(`Feature "${featureName}" ${parts.join(', ')}`);

      onSuccess();
      onClose();
    } catch (error: any) {
      const msg = error.response?.data?.message || error.message || 'Failed to save changes';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const filteredRooms = roomSearch.trim()
    ? rooms.filter((r) =>
        r.name.toLowerCase().includes(roomSearch.toLowerCase()) ||
        (r.floorNumber || '').toLowerCase().includes(roomSearch.toLowerCase()) ||
        (r.roomType || '').toLowerCase().includes(roomSearch.toLowerCase()),
      )
    : rooms;

  const columns = [
    {
      title: 'Room',
      dataIndex: 'name',
      key: 'name',
      width: 140,
    },
    {
      title: 'Floor',
      dataIndex: 'floorNumber',
      key: 'floorNumber',
      render: (val: string | null) =>
        val ? <Tag color="blue">{val}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Room Type',
      dataIndex: 'roomType',
      key: 'roomType',
      render: (val?: string) => val || <Text type="secondary">—</Text>,
    },
  ];

  const submitLabel = hasChanges
    ? `Save Changes (+${toAdd.length} / −${toRemove.length})`
    : 'No Changes';

  return (
    <Modal
      title={`Manage Feature Assignment: ${featureName}`}
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
          disabled={!hasChanges}
        >
          {submitLabel}
        </Button>,
      ]}
    >
      <Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Tick rooms that should have this feature. Unticking a room that currently has it will remove the feature from that room.
      </Paragraph>

      <Space direction="vertical" size="small" style={{ width: '100%', marginBottom: 12 }}>
        {(toAdd.length > 0 || toRemove.length > 0) && (
          <Alert
            type="info"
            showIcon
            message="Pending changes"
            description={
              <span>
                {toAdd.length > 0 && <span>Will add to <strong>{toAdd.length}</strong> room(s). </span>}
                {toRemove.length > 0 && <span>Will remove from <strong>{toRemove.length}</strong> room(s).</span>}
              </span>
            }
          />
        )}
        <Search
          placeholder="Search rooms..."
          allowClear
          value={roomSearch}
          onChange={(e) => setRoomSearch(e.target.value)}
          style={{ width: 300 }}
        />
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={loadingRooms}
        dataSource={filteredRooms}
        columns={columns}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        scroll={{ y: 320 }}
        rowSelection={{
          selectedRowKeys: selectedRoomIds,
          onChange: (keys) => setSelectedRoomIds(keys as number[]),
          preserveSelectedRowKeys: true,
        }}
      />
    </Modal>
  );
};

export default AssignFeatureToRoomsModal;
